import { ArrowRight } from "lucide-react";
import { Button } from "../ui/button";
import { priceListCopy } from "../legal/priceListCopy";
import { PriceListPlans } from "../pricelist/PriceListPlans";
import { useLanguage } from "../../i18n/language-provider";

interface PricingSectionProps {
    /** The anchor the header's link points at. */
    id: string;
    /** The full price list: the document the payment gateway reviews. */
    priceListHref: string;
    /** Starts sign-up, as the page's other sign-up buttons do. */
    onSignUp: () => void;
}

/**
 * "What does it cost?": the two products and their prices, on the page itself
 * (5 Oct 2026). The header's link used to leave for the price list's own
 * page, which is where a visitor weighing Optivo against another tool met a
 * document of nested bullets; a price for the property, what each room adds
 * and one worked example answer in one look, so they stand here.
 *
 * The section states no price of its own. It draws the price sheet
 * (`pricelist/PriceListPlans.tsx`) from its copy (`legal/priceListCopy.ts`,
 * built from the price list the billing engine is held to), so the page
 * cannot quote a price the checkout does not charge. The contract is
 * counsel's price list at /subscription, which the sheet must agree with. Its own words are the heading, one sentence under it, what the
 * trial asks for and the name of the link.
 *
 * One way in, and it is the page's own: sign-up carries no product, because
 * nobody chooses one — a property is priced by what it is. How the trial,
 * the payment and the cancellation work stays in the document, a link away.
 */
export function PricingSection({ id, priceListHref, onSignUp }: PricingSectionProps) {
    const { t, language } = useLanguage();
    const w = t.welcome;

    return (
        // Toned, between two plain bands: the count of priced rooms above it and the
        // closing band below.
        <section id={id} className="scroll-mt-20 border-t bg-card/60">
            <div className="mx-auto w-full max-w-6xl px-6 py-20 lg:py-24">
                <div className="max-w-2xl">
                    <p className="type-eyebrow">{w.navPricing}</p>
                    <h2 className="type-display-section mt-3 text-3xl text-balance">{w.pricingTitle}</h2>
                    <p className="mt-4 leading-relaxed text-muted-foreground">{w.pricingDescription}</p>
                </div>

                {/* The sheet lays itself out by its own width. The row under
                    it holds one unbreakable thing, the button, beside text
                    that wraps, so it follows the window as the page's other
                    button rows do. */}
                <PriceListPlans copy={priceListCopy(language)} headingLevel={3} className="mt-12">
                    <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between lg:gap-8">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-5">
                            <Button size="lg" className="h-12 px-6 text-base" onClick={onSignUp}>
                                {w.ctaCreate}
                                <ArrowRight className="ml-2 h-4 w-4" />
                            </Button>
                            <p className="max-w-md text-sm leading-relaxed text-muted-foreground">{w.pricingTrial}</p>
                        </div>

                        {/* Quiet beside the button: the section's one action
                            is signing up. `min-h-10` gives a finger the hit
                            box on a phone without moving the baseline. */}
                        <a
                            href={priceListHref}
                            className="inline-flex min-h-10 items-center self-start rounded-sm text-sm text-muted-foreground underline underline-offset-4 transition-colors hover:text-foreground sm:min-h-0 lg:shrink-0 lg:self-auto"
                        >
                            {w.pricingFullList}
                        </a>
                    </div>
                </PriceListPlans>
            </div>
        </section>
    );
}
